import { PRIORITY_ORDER } from '../../shared/services/helpers/ticket-rules.helper';
import { TicketSlaState } from '../../shared/models/ticket.model';
import { TicketUiModel } from '../../shared/ui-models/ticket.ui-model';

export type QueueSort = 'updated' | 'priority' | 'created' | 'due';

/**
 * Queue order (CTO, 2026-10-13: by last updated). 'updated' = most recently updated first,
 * the default. 'priority' = emergencies, then P1 to P4, then the longest waiting.
 * 'created' = newest first. 'due' = the nearest SLA due time first (overdue ones lead),
 * tickets with nothing due last.
 */
export function sortQueue(tickets: TicketUiModel[], by: QueueSort = 'updated'): TicketUiModel[] {
  const cmp: Record<QueueSort, (a: TicketUiModel, b: TicketUiModel) => number> = {
    updated: (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
    created: (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    due: (a, b) =>
      (a.sla.nextDue?.getTime() ?? Infinity) - (b.sla.nextDue?.getTime() ?? Infinity) || 0,
    priority: (a, b) =>
      Number(a.isClosed) - Number(b.isClosed) ||
      Number(b.emergency) - Number(a.emergency) ||
      PRIORITY_ORDER.indexOf(b.priority) - PRIORITY_ORDER.indexOf(a.priority) ||
      a.updatedAt.getTime() - b.updatedAt.getTime()
  };
  return [...tickets].sort((a, b) => cmp[by](a, b) || b.id - a.id);
}

/** The filter bar's state. Empty values mean "any". Dates are YYYY-MM-DD, local (Cairo). */
export interface QueueFilters {
  text: string;
  team: string;
  /** Owner name, or UNASSIGNED. */
  member: string;
  priority: string;
  state: '' | 'open' | 'closed';
  category: string;
  /** The ticket's overall SLA state. */
  sla: '' | TicketSlaState;
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
  emergencyOnly: boolean;
  newOnly: boolean;
}

export const UNASSIGNED = '__unassigned__';

export const EMPTY_FILTERS: QueueFilters = {
  text: '',
  team: '',
  member: '',
  priority: '',
  state: '',
  category: '',
  sla: '',
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: '',
  emergencyOnly: false,
  newOnly: false
};

/** How many filters are set (the "Clear (3)" button), not counting the text box. */
export const activeFilterCount = (f: QueueFilters): number =>
  (Object.keys(EMPTY_FILTERS) as (keyof QueueFilters)[]).filter(
    (k) => k !== 'text' && f[k] !== EMPTY_FILTERS[k]
  ).length;

const day = (d: Date): string => {
  const z = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};

/** Inclusive day range; '' = open-ended. Compared as local calendar days. */
const inRange = (d: Date, from: string, to: string) =>
  (!from || day(d) >= from) && (!to || day(d) <= to);

/**
 * Applies the filter bar to the loaded queue. Client-side: a queue is one Zammad overview
 * the caller can see, so this narrows what is already on screen.
 */
export function filterQueue(
  tickets: TicketUiModel[],
  f: QueueFilters,
  arrived: Set<number> = new Set()
): TicketUiModel[] {
  const needle = f.text.trim().toLowerCase();
  return tickets.filter(
    (t) =>
      (!needle ||
        [t.number, t.title, t.categoryLabel, t.categoryCode, t.team, t.ownerName, t.reservationRef]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle))) &&
      (!f.team || t.team === f.team) &&
      (!f.member || (f.member === UNASSIGNED ? !t.ownerName : t.ownerName === f.member)) &&
      (!f.priority || t.priority === f.priority) &&
      (!f.state || (f.state === 'closed') === t.isClosed) &&
      (!f.category || t.categoryCode === f.category) &&
      (!f.sla || t.sla.state === f.sla) &&
      inRange(t.createdAt, f.createdFrom, f.createdTo) &&
      inRange(t.updatedAt, f.updatedFrom, f.updatedTo) &&
      (!f.emergencyOnly || t.emergency) &&
      (!f.newOnly || arrived.has(t.id))
  );
}

/** The distinct values the dropdowns offer, from the loaded queue. */
export function filterOptions(tickets: TicketUiModel[]) {
  const uniq = (xs: (string | undefined)[]) =>
    Array.from(new Set(xs.filter((x): x is string => !!x))).sort((a, b) => a.localeCompare(b));
  return {
    teams: uniq(tickets.map((t) => t.team)),
    members: uniq(tickets.map((t) => t.ownerName)),
    categories: uniq(tickets.map((t) => t.categoryCode))
  };
}

/**
 * Tickets that arrived since the last refresh of the SAME queue. The first load of a queue
 * (prev = null) marks nothing new: switching tabs is not an arrival.
 */
export function newArrivals(prev: Set<number> | null, tickets: TicketUiModel[]): number[] {
  if (!prev) return [];
  return tickets.filter((t) => !prev.has(t.id)).map((t) => t.id);
}

/** How often the queue refreshes itself while it is on screen. */
export const QUEUE_REFRESH_MS = 30_000;
/** How long an arrival stays highlighted, unless opened first. */
export const NEW_HIGHLIGHT_MS = 5 * 60_000;

/** What the queue remembers for the browser tab: the URL search (tab, include closed), filters, sort. */
export interface SavedQueueState {
  search: string;
  filters: QueueFilters;
  sort: QueueSort;
}

const QUEUE_STATE_KEY = 'tickets.queue.state';

/**
 * The queue as it was last left in this browser tab, so opening a ticket and coming back
 * (by back, the menu, or a handoff) keeps the filters. "New only" is not kept: arrivals
 * are counted afresh on every visit.
 */
export function loadQueueState(): Partial<SavedQueueState> {
  try {
    const saved = JSON.parse(sessionStorage.getItem(QUEUE_STATE_KEY) ?? '{}');
    return {
      search: typeof saved.search === 'string' ? saved.search : undefined,
      filters: saved.filters ? { ...EMPTY_FILTERS, ...saved.filters, newOnly: false } : undefined,
      sort: saved.sort
    };
  } catch {
    return {};
  }
}

export function saveQueueState(state: SavedQueueState): void {
  try {
    sessionStorage.setItem(QUEUE_STATE_KEY, JSON.stringify(state));
  } catch {
    // Storage blocked (private mode): the queue just starts fresh next time.
  }
}
